/**
 * BrandedAlertModal — shared modal shell that matches the Figma reference
 * used by T2-5767 (Post a Shift confirm) and T2-5773 (Payment Required).
 *
 * Visual structure:
 *   [grey outer shell]
 *     [header: small label + optional close X]
 *     [white inner card]
 *       [optional illustration]
 *       [optional title]
 *       [body content (string or React node)]
 *       [divider + actions row]
 */

import React, { useEffect, useRef } from 'react';
import styles from './BrandedAlertModal.module.scss';

export type BrandedAction = {
  label: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  ariaLabel?: string;
};

export type BrandedIllustrationVariant = 'success' | 'danger' | 'info';

export interface BrandedAlertModalProps {
  isOpen: boolean;
  onClose?: () => void;
  /** Small grey label that appears in the modal header bar. */
  headerLabel?: React.ReactNode;
  /** Optional 18x18 SVG node rendered next to the header label. */
  headerIcon?: React.ReactNode;
  /** Show the X close button in the header. Defaults to true if onClose is provided. */
  showClose?: boolean;
  /** Optional centered illustration rendered inside the white inner card. */
  illustration?: React.ReactNode;
  /** Color theme for the default circular illustration background. */
  illustrationVariant?: BrandedIllustrationVariant;
  /** Optional bold title above the body. */
  title?: React.ReactNode;
  /** Body content. Can be a string or any node. */
  children: React.ReactNode;
  /** Buttons rendered in the action row. */
  actions: BrandedAction[];
  /** Stack actions vertically (e.g. 3+ long labels). Default is a horizontal row. */
  stackActions?: boolean;
  /** Optional aria label for the dialog. */
  ariaLabel?: string;
}

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BrandedAlertModal: React.FC<BrandedAlertModalProps> = ({
  isOpen,
  onClose,
  headerLabel,
  headerIcon,
  showClose,
  illustration,
  illustrationVariant = 'success',
  title,
  children,
  actions,
  stackActions = false,
  ariaLabel,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const getFocusableElements = () => Array.from(
      modalRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), '
        + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || [],
    ).filter(element => element.getAttribute('aria-hidden') !== 'true');

    const focusModal = () => {
      const focusableElements = getFocusableElements();
      (focusableElements[0] || modalRef.current)?.focus();
    };
    focusModal();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onCloseRef.current) {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        modalRef.current?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      const focusIsInside = !!modalRef.current?.contains(activeElement);

      if (!focusIsInside || (event.shiftKey && activeElement === firstElement)) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const previouslyFocused = previouslyFocusedRef.current;
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
      previouslyFocusedRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const showHeader = headerLabel != null || (showClose ?? !!onClose);
  const renderClose = (showClose ?? !!onClose) && !!onClose;

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose} />
      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        {showHeader && (
          <div className={styles.modalHeader}>
            <span className={styles.headerTitle}>
              {headerIcon ? <span className={styles.headerIcon}>{headerIcon}</span> : null}
              {headerLabel}
            </span>
            {renderClose && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}

        <div className={styles.innerCard}>
          {illustration && (
            <div
              className={[
                styles.illustration,
                illustrationVariant === 'danger' ? styles.illustrationDanger : '',
                illustrationVariant === 'info' ? styles.illustrationInfo : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {illustration}
            </div>
          )}

          {title && <h2 className={styles.modalTitle}>{title}</h2>}

          <div className={styles.modalText}>{children}</div>

          {actions && actions.length > 0 && (
            <div
              className={[
                styles.modalActions,
                stackActions ? styles.modalActionsStacked : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {actions.map((action, idx) => {
                const variant = action.variant === 'secondary' ? 'secondary' : 'primary';
                return (
                  <button
                    key={idx}
                    type="button"
                    className={
                      variant === 'primary' ? styles.primaryButton : styles.secondaryButton
                    }
                    onClick={action.onClick}
                    disabled={action.disabled}
                    aria-label={action.ariaLabel}
                  >
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BrandedAlertModal;
